/**
 * Utility function to calculate evenly spaced date ticks for chart X-axis
 * @param data Array of data objects with a 'date' property
 * @returns Array of date strings to use as ticks
 */
export function getDateTicks<T extends { date: string }>(data: T[]): string[] {
  if (!data || data.length === 0) return [];

  // Normalize dates to YYYY-MM-DD format for comparison
  const normalizeDate = (dateStr: string) => {
    if (!dateStr) return "";
    // If date includes time, take only the first 10 characters (YYYY-MM-DD)
    return dateStr.length > 10 ? dateStr.substring(0, 10) : dateStr;
  };

  // Get unique dates from data (in case of duplicates)
  const uniqueDates = new Set<string>();
  const dateToIndex = new Map<string, number>();
  data.forEach((item, index) => {
    const normalized = normalizeDate(item.date);
    if (normalized && !uniqueDates.has(normalized)) {
      uniqueDates.add(normalized);
      dateToIndex.set(normalized, index);
    }
  });

  const uniqueDatesArray = Array.from(uniqueDates).sort();
  const uniqueLastIndex = uniqueDatesArray.length - 1;

  // Show approximately 8-10 ticks for better readability
  // We'll show evenly spaced dates, excluding the last date from spacing calculation
  const datesToSpace = Math.max(1, uniqueLastIndex); // All dates except we'll add last separately
  const desiredTicks = Math.min(10, Math.max(5, Math.floor(datesToSpace / 3)));
  const step = Math.max(1, Math.floor(datesToSpace / desiredTicks));

  const ticks: string[] = [];

  // Always include first date
  if (uniqueDatesArray[0]) {
    ticks.push(uniqueDatesArray[0]);
  }

  // Add evenly spaced dates, stopping before the last date
  const stopIndex = uniqueLastIndex;
  for (let i = step; i < stopIndex; i += step) {
    if (uniqueDatesArray[i]) {
      ticks.push(uniqueDatesArray[i]);
    }
  }

  // Always include the very last date - ensure it's added and not duplicated
  if (uniqueDatesArray[uniqueLastIndex]) {
    const lastDate = uniqueDatesArray[uniqueLastIndex];
    // Remove if somehow already included, then add at the end
    const filteredTicks = ticks.filter(
      (t) => normalizeDate(t) !== normalizeDate(lastDate),
    );
    filteredTicks.push(lastDate);
    return filteredTicks;
  }

  return ticks;
}
